import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import {
  DropOffLocations,
  DropOffLocationsVariables
} from "./types/DropOffLocations";
import {
  DropOffMallQuery,
  DropOffMallQueryVariables
} from "./types/DropOffMallQuery";
import {
  GetAvailableTemplates,
  GetAvailableTemplatesVariables
} from "./types/GetAvailableTemplates";
import {
  GetMallTenants,
  GetMallTenantsVariables
} from "./types/GetMallTenants";
import { GetTemplate, GetTemplateVariables } from "./types/GetTemplate";
import { GetTenants, GetTenantsVariables } from "./types/GetTenants";
import {
  MallListDropOff,
  MallListDropOffVariables
} from "./types/MallListDropOff";
import { RacksQuery, RacksQueryVariables } from "./types/RacksQuery";

const dropOffLocationQuery = gql`
  query DropOffLocations(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: DropOffLocationInput
  ) {
    dropOffLocations(
      first: $first
      after: $after
      last: $last
      before: $before
      filter: $filter
    ) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          name
          code
          status
          description
          firstName
          lastName
          contactNo
          latitude
          longitude
          dropOffAddress
          remarks
          outletId
          template {
            id
            name
            status
          }
        }
      }
    }
  }
`;
export const useDropOffListQuery = makeQuery<
  DropOffLocations,
  DropOffLocationsVariables
>(dropOffLocationQuery);

const mallListDropOffQuery = gql`
  query MallListDropOff(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filter: MallInput
  ) {
    malls(
      first: $first
      after: $after
      last: $last
      before: $before
      filter: $filter
    ) {
      edges {
        node {
          id
          name
          code
          mallOpening
          mallClosing
          templates {
            totalCount
          }
          dropOffLocations(first: 100) {
            edges {
              node {
                id
                name
                code
                status
                template {
                  id
                  name
                  assignedStores {
                    totalCount
                  }
                  taskAreas(first: 100) {
                    edges {
                      node {
                        id
                        name
                        tenants {
                          totalCount
                        }
                      }
                    }
                  }
                }
                taskAreas {
                  id
                  name
                  runners {
                    id
                    firstName
                    lastName
                    isActive
                  }
                  tenants {
                    totalCount
                  }
                }
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;
export const useMallListDropOffQuery = makeQuery<
  MallListDropOff,
  MallListDropOffVariables
>(mallListDropOffQuery);

const getTemplateQuery = gql`
  query GetTemplate(
    $id: ID!
    $first: Int
    $after: String
    $last: Int
    $before: String
    $tenantSearch: String
    $tenantStoreType: StoreTypeEnum
    $dropOffSortBy: DropOffLocationSortingInput
  ) {
    template(id: $id) {
      id
      name
      status
      taskAreas(first: 100) {
        totalCount
        edges {
          node {
            dropOffLocation {
              id
              name
            }
            id
            name
            status
            createdAt
            updatedAt
            tenants(
              first: $first
              after: $after
              last: $last
              before: $before
              filter: { search: $tenantSearch, storeType: $tenantStoreType }
            ) {
              totalCount
              pageInfo {
                hasNextPage
                hasPreviousPage
                endCursor
                startCursor
              }
              edges {
                node {
                  id
                  storeName
                  status
                }
              }
            }
          }
        }
      }
      dropOffLocations(first: 100, sortBy: $dropOffSortBy) {
        edges {
          node {
            id
            name
            code
            status
            defaultDropOffLocation
            contactNo
            dropOffAddress
            latitude
            longitude
            remarks
            firstName
            lastName
            taskAreas {
              id
              name
              status
            }
            outletId
            updatedAt
          }
        }
      }
      racks(first: 100) {
        totalCount
      }
    }
  }
`;
export const useGetTemplateQuery = makeQuery<GetTemplate, GetTemplateVariables>(
  getTemplateQuery
);

const getAvailableTemplatesQuery = gql`
  query GetAvailableTemplates($first: Int, $filter: TemplateInput, $last: Int) {
    templates(first: $first, filter: $filter, last: $last) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          name
          status
          mall {
            id
            name
            mallOpening
            mallClosing
          }
          taskAreas(first: $first) {
            totalCount
            edges {
              node {
                name
              }
            }
          }
          dropOffLocations(first: $first) {
            totalCount
            edges {
              node {
                name
                outletId
              }
            }
          }
          assignedStores(first: $first) {
            totalCount
          }
        }
      }
    }
  }
`;
export const useGetAvailableTemplatesQuery = makeQuery<
  GetAvailableTemplates,
  GetAvailableTemplatesVariables
>(getAvailableTemplatesQuery);

const getMallTenantsQuery = gql`
  query GetMallTenants($id: ID!, $tenantStatus: SmopStatusEnum) {
    mall(id: $id) {
      id
      name
      code
      status
      tenants(status: $tenantStatus) {
        id
        storeName
        legalName
        status
        taskAreas(first: 100) {
          totalCount
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
  }
`;
export const useGeMallTenantsQuery = makeQuery<
  GetMallTenants,
  GetMallTenantsVariables
>(getMallTenantsQuery);

const getTenantsQuery = gql`
  query GetTenants(
    $mallId: ID
    $status: SmopStatusEnum
    $storeType: StoreTypeEnum
    $first: Int
    $after: String
    $last: Int
    $before: String
    $search: String
    $isNotAssignedInTemplates: [ID]
  ) {
    tenants(
      mall: $mallId
      status: $status
      first: $first
      after: $after
      last: $last
      before: $before
      filter: {
        storeType: $storeType
        search: $search
        isNotAssignedInTemplates: $isNotAssignedInTemplates
      }
    ) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        node {
          id
          storeName
          legalName
          status
          taskAreas(first: 100) {
            totalCount
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;
export const useGetTenantsQuery = makeQuery<GetTenants, GetTenantsVariables>(
  getTenantsQuery
);

const dropOffMallQuery = gql`
  query DropOffMallQuery($id: ID!) {
    mall(id: $id) {
      id
      name
      code
      mallOpening
      mallClosing
      dropOffLocations(first: 100) {
        edges {
          node {
            id
            name
            code
            status
            template {
              id
              name
              assignedStores {
                totalCount
              }
              taskAreas(first: 100) {
                edges {
                  node {
                    id
                    name
                    tenants {
                      totalCount
                    }
                  }
                }
              }
            }
            taskAreas {
              id
              name
              runners {
                id
                firstName
                lastName
                isActive
              }
              tenants {
                totalCount
              }
            }
          }
        }
      }
    }
  }
`;
export const useDropOffMallQuery = makeQuery<
  DropOffMallQuery,
  DropOffMallQueryVariables
>(dropOffMallQuery);

const racksQuery = gql`
  query RacksQuery(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $template: ID
    $dropOffLocation: ID
  ) {
    racks(
      filter: { template: $template, dropOffLocation: $dropOffLocation }
      before: $before
      last: $last
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          name
          prefix
          bins(first: $first) {
            totalCount
            edges {
              node {
                id
                binNo
                isAvailable
              }
            }
          }
        }
      }
    }
  }
`;
export const useRacksQuery = makeQuery<RacksQuery, RacksQueryVariables>(
  racksQuery
);
